import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { API_BASE_URL } from "config";
import { useNavigate } from "react-router-dom";
import { selectIsAuthenticated } from "store/auth/selectors";
import BookingCard from "views/BookingsListPage/BookingCard";
import { getBookingsHistory } from "services/dashboard.services";
import {
  NoResultsSection,
  SkeletonBookingCard,
  FiltersDropdown,
  Pagination,
} from "reusableComponents";

import styles from "./styles.module.scss";

const BookingsHistory = () => {
  const { t } = useTranslation("global");
  const [error, setError] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationData, setPaginationData] = useState(null);

const baseUrl= API_BASE_URL;

  const [orderBy, setOrderBy] = useState("date");
  const [order, setOrder] = useState("desc");
  const [fdate, setFdate] = useState("all"); // New state for date filtering

  const isAuthenticated = useSelector(selectIsAuthenticated);
  const accessToken = useSelector((state) => state.auth.token);
  const navigate = useNavigate();

  const exportUrl = `${baseUrl}users/bookings/past/export?fdate=${fdate}&order_by=${orderBy}&order=${order}&token=${accessToken}`;
  const itemsPerPage = 8;

  useEffect(() => {
    const getBookings = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getBookingsHistory(accessToken, {
          page: currentPage,
          perPage: itemsPerPage,
          orderBy,
          order,
          fdate, // Include fdate in the API call
        });
        setBookings(data.data);
        setPaginationData(data.pagination);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      getBookings();
    } else {
      navigate("/");
    }
  }, [
    accessToken,
    currentPage,
    orderBy,
    order,
    fdate,
    isAuthenticated,
    navigate,
  ]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const orderByOptions = [
    { value: "date", label: t("dashboard.history.date") },
    { value: "price", label: t("dashboard.history.price") },
    { value: "city", label: t("dashboard.history.city") },
    { value: "space", label: t("dashboard.history.space") },
  ];

  const fdateOptions = [
    { value: "all", label: t("dashboard.history.all") },
    { value: "last_month", label: t("dashboard.history.lastMonth") },
    { value: "last_3_months", label: t("dashboard.history.last3Months") },
    { value: "current_year", label: t("dashboard.history.currentYear") },
    { value: "previous_year", label: t("dashboard.history.previousYear") },
  ];

  if (error) {
    return <div className={styles.error}>{error}</div>;
  }

  return (
    <div
      className={`${styles.bookingsHistoryPage} ${
        loading ? styles.loading : ""
      }`}
    >
      <h1 className={styles.title}>{t("dashboard.history.title")}</h1>
      {/* Filters for sorting */}
      <div className={styles.filters}>
        <FiltersDropdown
          label={t("dashboard.history.orderBy")}
          options={orderByOptions}
          selected={orderBy}
          onSelect={setOrderBy}
          placeholder={t("dashboard.bookings.sortByPlaceholder")}
        />
        <FiltersDropdown
          label={t("dashboard.history.show")}
          options={fdateOptions} // New date filtering dropdown
          selected={fdate}
          onSelect={setFdate}
          placeholder={t("dashboard.bookings.dateFilterPlaceholder")}
        />
        <a
          className={styles.exportLink}
          href={exportUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("dashboard.history.export")}
        </a>
      </div>
      {bookings.length === 0 && !loading ? (
        <NoResultsSection
          title={t("dashboard.bookings.noResultTitle")}
          text={t("dashboard.bookings.noResultText")}
        />
      ) : loading ? (
        <div className={styles.bookingsList}>
          {Array.from({ length: 5 }).map((_, index) => (
            <SkeletonBookingCard key={index} />
          ))}
        </div>
      ) : (
        <>
          <div className={styles.bookingsList}>
            {bookings.map((booking) => (
              <BookingCard key={booking.id} booking={booking} layout="bookingHistory" />
            ))}
          </div>
          {paginationData && (
            <Pagination
              totalItems={paginationData.total}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              hasMorePages={paginationData.has_more_pages}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}
    </div>
  );
};

export default BookingsHistory;
